/* Interview Clozo — "Ignition", the web celebrations (owner rulings D12.13 + D13.9; CONTRACT R5 / R33; SITE §4).
   Exposes window.clozoIgnition(opts). Loaded ON DEMAND only — by success.js after R5 confirmed a celebration, and by
   billing-ui.js celebrate() on the account plan card — never at first paint and never on a page view by itself.

   WHAT IT DOES
   - Plays ONE short celebration for one payment event: a trial start, a new subscription, a Monthly -> Yearly switch, a
     user renewal (success page, "full"), or an account-page moment (plan card, "card"). The words are the page's; this
     file draws only the motion. There is NO badge, NO "Member" / "Founding Member" pill and NO "You save" line (D13.9).
   - Once per event: an id that already played in this page is ignored (the pages also keep the last 20 ids in
     localStorage.clozo_celebrated, and the server marks the row shown, R5 / R33).
   - Reduced motion (prefers-reduced-motion: reduce): the confetti file is NEVER requested; the check simply appears and
     onDone runs after the 200 ms cross-fade. The words are identical.
   - The confetti is the self-hosted canvas-confetti 1.9.4 (assets/js/vendor/, ISC) drawn on one fixed, click-through,
     aria-hidden canvas with useWorker:false (the site CSP has no worker-src blob:). Brand colours only. Nothing moves
     after 3 s; the canvas is removed at 3.2 s. No sound on the web.

   opts = { kind, planID, size: 'full' | 'card', id, slot, delayed, onDone }
     kind   CONTRACT celebrationKind: trial_started | subscribed | trial_converted | switched_yearly | renewed_manual |
            renewed_auto
     planID 'monthly' | 'yearly' (picks the bigger "Crowning" beat for Yearly)
     slot   the element the burst starts from (success: the 96 px ring; account: the plan-card line)
   Returns a Promise that resolves when the celebration has finished (after onDone). */
(function () {
  'use strict';
  if (typeof window.clozoIgnition === 'function') return;

  var CONFETTI_DEFAULT = '/assets/js/vendor/canvas-confetti-1.9.4.js';
  var COLORS = ['#22d3ee', '#a78bfa', '#fbbf24', '#34d399'];
  var AMBER = ['#fbbf24', '#fde68a'];
  var played = {};
  var loading = null;

  function reducedMotion() {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) { return false; }
  }

  /* The bake stamps data-confetti-src with a content hash (?v=); the constant is the same versioned file name. */
  function confettiSrc() {
    var holder = document.querySelector('[data-confetti-src]');
    var src = holder && holder.getAttribute('data-confetti-src');
    return src && src.indexOf('/assets/js/vendor/canvas-confetti-') === 0 ? src : CONFETTI_DEFAULT;
  }

  function loadConfetti() {
    if (window.confetti && typeof window.confetti.create === 'function') return Promise.resolve(window.confetti);
    if (loading) return loading;
    loading = new Promise(function (resolve) {
      var s = document.createElement('script');
      s.src = confettiSrc();
      s.async = true;
      s.onload = function () { resolve(window.confetti && typeof window.confetti.create === 'function' ? window.confetti : null); };
      s.onerror = function () { loading = null; resolve(null); };
      document.head.appendChild(s);
    });
    return loading;
  }

  /* Which beat: Spark (trial start), Crowning (anything Yearly), High-five-plus (Monthly) on the success page;
     High-five on the account plan card. Timings = SITE §4.3 without the removed badge beat. */
  function variantFor(o) {
    if (o.size === 'card') return 'highfive';
    if (o.kind === 'trial_started') return 'spark';
    if (o.kind === 'switched_yearly' || o.planID === 'yearly') return 'crowning';
    return 'highfiveplus';
  }
  var DURATION = { spark: 1600, highfive: 1200, highfiveplus: 2500, crowning: 3000 };

  function origin(slot) {
    try {
      var b = slot.getBoundingClientRect();
      var w = window.innerWidth || document.documentElement.clientWidth || 1;
      var h = window.innerHeight || document.documentElement.clientHeight || 1;
      return { x: Math.min(1, Math.max(0, (b.left + b.width / 2) / w)), y: Math.min(1, Math.max(0, (b.top + b.height / 2) / h)) };
    } catch (_) { return { x: 0.5, y: 0.4 }; }
  }

  function makeCanvas() {
    var c = document.createElement('canvas');
    c.className = 'cb-confetti';
    c.setAttribute('aria-hidden', 'true');
    c.style.position = 'fixed';
    c.style.left = '0';
    c.style.top = '0';
    c.style.width = '100%';
    c.style.height = '100%';
    c.style.pointerEvents = 'none';
    c.style.zIndex = '60';
    document.body.appendChild(c);
    return c;
  }

  function spark(confettiLib) {
    try {
      return confettiLib.shapeFromPath({ path: 'M0 -10 L2 -2 L10 0 L2 2 L0 10 L-2 2 L-10 0 L-2 -2 Z' });
    } catch (_) { return null; }
  }

  /* The ring pop on the success page's pre-reserved slot (transform only: no layout shift). */
  function pop(slot) {
    try {
      var ring = slot.querySelector('.cb-ring') || slot;
      if (ring.animate) ring.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.12)' }, { transform: 'scale(1)' }], { duration: 420, easing: 'ease-out' });
    } catch (_) {}
  }

  function run(confettiLib, o, variant) {
    var canvas = makeCanvas();
    var fire = confettiLib.create(canvas, { resize: true, useWorker: false, disableForReducedMotion: true });
    var at = origin(o.slot);
    var shapes = ['square', 'circle'];
    var sp = spark(confettiLib);
    if (sp) shapes = shapes.concat([sp]);
    var timers = [];
    function later(ms, fn) { timers.push(setTimeout(function () { try { fn(); } catch (_) {} }, ms)); }
    var base = { colors: COLORS, shapes: shapes, ticks: 170, disableForReducedMotion: true };
    function burst(extra) { var opts = {}; var k; for (k in base) opts[k] = base[k]; for (k in extra) opts[k] = extra[k]; return fire(opts); }

    if (variant === 'highfive') {
      // Account plan card: one 40-particle burst from the card line (1.2 s).
      later(120, function () { burst({ particleCount: 40, spread: 70, startVelocity: 32, origin: at, scalar: 0.9 }); });
    } else if (variant === 'spark') {
      // Trial start: the check draws, then ONE 60-particle burst from the slot centre, done at 1.6 s.
      later(250, function () { pop(o.slot); });
      later(300, function () { burst({ particleCount: 60, spread: 70, startVelocity: 40, origin: at }); });
    } else {
      // High-five-plus (Monthly) and Crowning (Yearly): two side cannons from the bottom corners, 45 particles each.
      later(250, function () { pop(o.slot); });
      later(450, function () {
        burst({ particleCount: 45, angle: 60, spread: 55, startVelocity: 55, origin: { x: 0, y: 0.9 } });
        burst({ particleCount: 45, angle: 120, spread: 55, startVelocity: 55, origin: { x: 1, y: 0.9 } });
      });
      if (variant === 'crowning') {
        // The Crowning beat: 30 amber sparks from the slot (no badge, no savings line — D13.9).
        later(1600, function () { burst({ particleCount: 30, spread: 100, startVelocity: 25, gravity: 0.6, scalar: 0.8, colors: AMBER, origin: at }); });
      }
    }
    var total = DURATION[variant] || 2500;
    return new Promise(function (resolve) {
      later(Math.max(total, 1200), function () {
        try { fire.reset(); } catch (_) {}
        setTimeout(function () { if (canvas.parentNode) canvas.parentNode.removeChild(canvas); }, 200);
        resolve();
      });
    });
  }

  function clozoIgnition(opts) {
    var o = opts || {};
    var done = typeof o.onDone === 'function' ? o.onDone : function () {};
    var finish = function () { try { done(); } catch (_) {} };
    if (o.id && played[o.id]) return Promise.resolve(false);
    if (o.id) played[o.id] = true;
    var slot = o.slot || document.body;
    o.slot = slot;
    var variant = variantFor(o);
    if (reducedMotion()) {
      // Words and the static check only; no confetti file is requested.
      return new Promise(function (resolve) { setTimeout(function () { finish(); resolve(true); }, 200); });
    }
    return loadConfetti().then(function (lib) {
      if (!lib) { finish(); return true; }
      return run(lib, o, variant).then(function () { finish(); return true; });
    }, function () { finish(); return true; });
  }

  clozoIgnition.variantFor = variantFor;
  clozoIgnition.DURATION = DURATION;
  window.clozoIgnition = clozoIgnition;
})();
